import { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Container, Grid, Typography, Stack, Avatar, Paper, Skeleton, LinearProgress } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { InsightsRounded, DateRangeRounded, VerifiedUserRounded } from '@mui/icons-material';
import { DatePicker, ConfigProvider, theme } from 'antd';
import { salesTargetDashboardApi } from 'utils/API/RelationManagerAPIs/RelationManagerAPiCalls';

// Components
import PerformanceCard from './components/PerformanceCard';
import TeamPerformanceTable from './components/TeamPerformanceTable';

// Mirror of the hierarchy in TeamPerformanceTable — used to resolve default role before first API call
const ROLE_HIERARCHY = {
    national_head: ['zone_head', 'state_head', 'middle_manager', 'manager'],
    zone_head: ['state_head', 'middle_manager', 'manager'],
    state_head: ['middle_manager', 'manager'],
    team_leader: ['manager'],
    frontline: []
};

/** Returns the first subordinate role for the given userRole, or null */
const getDefaultRole = (userRole) => {
    const options = ROLE_HIERARCHY[userRole];
    return options?.length ? options[0] : null;
};

const SalesTargetLandingPage = () => {
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [data, setData] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedRange, setSelectedRange] = useState(null);
    const [selectedView, setSelectedView] = useState('mtd'); // 'mtd', 'ftd', or null
    // Initialise from localStorage so the very first API call includes role_filter
    const [selectedRole, setSelectedRole] = useState(() => getDefaultRole(localStorage.getItem('subrole')));
    const isInitialLoad = useRef(true);

    const getdata = useCallback(async (date, type, roleFilter, range) => {
        const isFilterChange = !isInitialLoad.current && (date || type || range);

        if (isInitialLoad.current || isFilterChange) {
            setLoading(true);
            setData(null); // Clear old data to prevent stale animations
        } else {
            setUpdating(true);
        }
        try {
            let payload = {};
            if (type) {
                payload.filter = type.toUpperCase();
            } else if (range && range[0] && range[1]) {
                payload.from_date = range[0].format('YYYY-MM-DD');
                payload.to_date = range[1].format('YYYY-MM-DD');
            } else if (date) {
                payload.month = date.format('MM');
                payload.year = date.format('YYYY');
            }

            if (roleFilter) {
                payload.role_filter = roleFilter;
            }

            const response = await salesTargetDashboardApi(payload);
            if (response?.data) {
                setData(response?.data?.data);
            }
        } catch (error) {
            console.error('Error fetching sales target data:', error);
        } finally {
            setLoading(false);
            setUpdating(false);
            isInitialLoad.current = false;
        }
    }, []);

    useEffect(() => {
        getdata(selectedDate, selectedView, selectedRole, selectedRange);
    }, [getdata, selectedDate, selectedView, selectedRole, selectedRange]);

    const handleDateChange = (date) => {
        if (date) {
            setSelectedDate(date);
            setSelectedView(null);
            setSelectedRange(null);
        }
    };

    const handleRangeChange = (dates) => {
        if (dates && dates[0] && dates[1]) {
            setSelectedRange(dates);
            setSelectedDate(null);
            setSelectedView(null);
        } else {
            setSelectedRange(null);
            // Fallback to MTD when cleared
            setSelectedView('mtd');
        }
    };

    if (loading && !data) {
        return (
            <Container maxWidth="xl" sx={{ py: 6 }}>
                <Skeleton variant="rectangular" height={100} sx={{ borderRadius: '24px', mb: 4 }} />
                <Grid container spacing={3}>
                    {[1, 2, 3, 4].map((i) => (
                        <Grid item xs={12} sm={6} lg={3} key={i}>
                            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '24px' }} />
                        </Grid>
                    ))}
                </Grid>
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: '24px', mt: 4 }} />
            </Container>
        );
    }

    if (!data) return null;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: { xs: 3, md: 6 }, position: 'relative' }}>
            {updating && (
                <LinearProgress
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 9999,
                        height: 4,
                        bgcolor: 'rgba(59, 130, 246, 0.1)',
                        '& .MuiLinearProgress-bar': {
                            bgcolor: '#3b82f6'
                        }
                    }}
                />
            )}
            <Container maxWidth="xl">
                {/* Header */}
                <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 3, md: 5 },
                            borderRadius: '24px',
                            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', // Deep Enterprise Navy
                            color: '#fff',
                            mb: 6,
                            position: 'relative',
                            overflow: 'hidden',
                            boxShadow: '0 20px 50px -12px rgba(15, 23, 42, 0.4)',
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}
                    >
                        {/* Elegant Decorative Elements */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: -50,
                                right: -50,
                                width: 200,
                                height: 200,
                                background: 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)',
                                borderRadius: '50%',
                                pointerEvents: 'none',
                                zIndex: 0
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: -20,
                                left: '20%',
                                width: 150,
                                height: 150,
                                background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
                                borderRadius: '50%',
                                pointerEvents: 'none',
                                zIndex: 0
                            }}
                        />

                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={4}
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ position: 'relative', zIndex: 1 }}
                        >
                            <Stack direction="row" spacing={3} alignItems="center">
                                <Avatar
                                    sx={{
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        width: 80,
                                        height: 80,
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        backdropFilter: 'blur(10px)',
                                        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)'
                                    }}
                                >
                                    <VerifiedUserRounded sx={{ fontSize: 40, color: '#3b82f6' }} />
                                </Avatar>
                                <Box>
                                    <Typography
                                        variant="h2"
                                        color="white"
                                        fontWeight={900}
                                        sx={{
                                            letterSpacing: '-0.02em',
                                            textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                        }}
                                    >
                                        Sales Target Dashboard
                                    </Typography>
                                    <Stack direction="row" spacing={2.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 2 }}>
                                        <ConfigProvider
                                            theme={{
                                                algorithm: theme.darkAlgorithm,
                                                token: {
                                                    colorPrimary: '#3b82f6',
                                                    borderRadius: 10,
                                                    colorBgContainer: 'rgba(255, 255, 255, 0.08)',
                                                    colorBorder: 'rgba(255, 255, 255, 0.1)'
                                                }
                                            }}
                                        >
                                            <DatePicker
                                                picker="month"
                                                value={selectedDate}
                                                onChange={handleDateChange}
                                                allowClear={true}
                                                placeholder="By Month"
                                                suffixIcon={<DateRangeRounded sx={{ fontSize: 18, color: '#3b82f6' }} />}
                                                style={{
                                                    height: 36,
                                                    width: 140,
                                                    fontWeight: 700,
                                                    color: 'white'
                                                }}
                                            />
                                            <DatePicker.RangePicker
                                                value={selectedRange}
                                                onChange={handleRangeChange}
                                                allowClear={true}
                                                placeholder={['From Date', 'To Date']}
                                                suffixIcon={<DateRangeRounded sx={{ fontSize: 18, color: '#3b82f6' }} />}
                                                style={{
                                                    height: 36,
                                                    width: 260,
                                                    fontWeight: 700,
                                                    color: 'white'
                                                }}
                                            />
                                        </ConfigProvider>

                                        {/* Separate MTD & FTD Buttons */}
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Box
                                                onClick={() => {
                                                    setSelectedView('mtd');
                                                    setSelectedDate(null);
                                                    setSelectedRange(null);
                                                }}
                                                sx={{
                                                    px: 2,
                                                    py: 0.5,
                                                    borderRadius: '10px',
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    bgcolor: selectedView === 'mtd' ? '#3b82f6' : 'rgba(255, 255, 255, 0.05)',
                                                    color: selectedView === 'mtd' ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                                    border: '1px solid',
                                                    borderColor: selectedView === 'mtd' ? '#3b82f6' : 'rgba(255, 255, 255, 0.1)',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 800,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: 1,
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    boxShadow: selectedView === 'mtd' ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none',
                                                    '&:hover': {
                                                        color: 'white',
                                                        bgcolor: selectedView === 'mtd' ? '#2563eb' : 'rgba(255, 255, 255, 0.12)',
                                                        borderColor: selectedView === 'mtd' ? '#2563eb' : 'rgba(255, 255, 255, 0.2)'
                                                    }
                                                }}
                                            >
                                                MTD
                                            </Box>
                                            <Box
                                                onClick={() => {
                                                    setSelectedView('ftd');
                                                    setSelectedDate(null);
                                                    setSelectedRange(null);
                                                }}
                                                sx={{
                                                    px: 2,
                                                    py: 0.5,
                                                    borderRadius: '10px',
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    bgcolor: selectedView === 'ftd' ? '#3b82f6' : 'rgba(255, 255, 255, 0.05)',
                                                    color: selectedView === 'ftd' ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                                    border: '1px solid',
                                                    borderColor: selectedView === 'ftd' ? '#3b82f6' : 'rgba(255, 255, 255, 0.1)',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 800,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: 1,
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    boxShadow: selectedView === 'ftd' ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none',
                                                    '&:hover': {
                                                        color: 'white',
                                                        bgcolor: selectedView === 'ftd' ? '#2563eb' : 'rgba(255, 255, 255, 0.12)',
                                                        borderColor: selectedView === 'ftd' ? '#2563eb' : 'rgba(255, 255, 255, 0.2)'
                                                    }
                                                }}
                                            >
                                                FTD
                                            </Box>
                                        </Stack>

                                        <Box
                                            sx={{
                                                px: 2,
                                                py: 0.5,
                                                borderRadius: '10px',
                                                bgcolor: 'rgba(59, 130, 246, 0.15)',
                                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={800}
                                                sx={{
                                                    textTransform: 'uppercase',
                                                    letterSpacing: 1.5,
                                                    color: '#60a5fa',
                                                    fontSize: '0.75rem'
                                                }}
                                            >
                                                {data.role?.replace(/_/g, ' ')}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Box>
                            </Stack>

                            <Box sx={{ display: { xs: 'none', md: 'block' }, opacity: 0.4 }}>
                                <InsightsRounded sx={{ fontSize: 100 }} />
                            </Box>
                        </Stack>
                    </Paper>
                </motion.div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={selectedView || selectedDate?.format('YYYYMM')}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.4 }}
                    >
                        {/* Overall Performance Grid */}
                        <Typography
                            variant="h5"
                            fontWeight={800}
                            sx={{ mb: 4, px: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}
                        >
                            Personal Performance Summary
                        </Typography>
                        <Grid container spacing={3} sx={{ mb: 8 }}>
                            {data.performance &&
                                Object.entries(data.performance).map(([key, metrics], index) => (
                                    <Grid item xs={12} sm={6} lg={3} key={key}>
                                        <PerformanceCard metric={metrics.metric || key.replace(/_/g, ' ')} data={metrics} index={index} />
                                    </Grid>
                                ))}
                        </Grid>

                        {/* Team Performance Section - Strictly for Managers */}
                        {data.is_manager && data.team_performance && data.team_performance.length > 0 && (
                            <Box sx={{ mt: 8 }}>
                                <TeamPerformanceTable
                                    teamData={data.team_performance}
                                    userRole={data.role}
                                    selectedRole={selectedRole}
                                    onRoleChange={setSelectedRole}
                                />
                            </Box>
                        )}
                    </motion.div>
                </AnimatePresence>
            </Container>
        </Box>
    );
};

export default SalesTargetLandingPage;
